import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { useProjetoImpressaoFilters } from "../hooks/Impressao_listasolicitar/filter";
import { useProjetoImpressaoPagination } from "../hooks/Impressao_listasolicitar/pagination";
import { ProjetoImpressaoLista } from "../../domain/models/impressao_projeto_lista";
import { emailTemplates } from "../../../../common/utils/email_templates";
import { EmailForm } from "../../../../common/presentation/schemas/email";

type LoadProjetoImpressaoState = {
  idsProjeto?: number[];
  idsCentroCusto?: number[];
  idsSolicitante?: number[];
  status?: number;
  statusEmail?: number;
  dataDe?: Date | null;
  dataAte?: Date | null;

  page?: number | null;
  rowsPerPage?: number | null;
};

export const useImpressaoListaGerenciarViewModel = () => {

  const {
    projetos: {
      getAllProjetoImpressaoUseCase,
      sendEmailProjetoImpressaoUseCase,
      getEmailImpressaoParametersUseCase,
    },

    commons: {
      getCentroCustoDropDownUseCase,
      getFuncionariosDropDownUseCase,
      getProjetosDropDownUseCase,
    },

  } = useDependencyInjection();

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();
  const filters = useProjetoImpressaoFilters();
  const pagination = useProjetoImpressaoPagination();
  const debouncedLoad = useCreateDebounceHandler(750);

  const [listaImpressao, setListaImpressao] = useState<ProjetoImpressaoLista[]>();
  const [selectedIdProjetoImpressao, setSelectedIdProjetoImpressao] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>([]);
  const [ddlProjeto, setDdlProjeto] = useState<DropDown[]>([]);
  const [ddlFuncionario, setDdlFuncionario] = useState<DropDown[]>([]);

  const [ddlStatus, setDdlStatus] = useState<DropDown[]>([]);
  const [ddlStatusEmail, setDdlStatusEmail] = useState<DropDown[]>([]);

  const [emailSendTo, setEmailSendTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailAttachment, setEmailAttachment] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const [openEmailModal, setOpenEmailModal] = useState(false);


  const backOrNavigate = useBackOrNavigate();

  const statusOptions = [
    { title: "Todas", id: 3 },
    { title: "Pendente", id: 0 },
    { title: "Aprovadas", id: 1 },
    { title: "Reprovadas", id: 2 },
  ];

  const statusEmailOptions = [
    { title: "Todas", id: 2 },
    { title: "Não Enviada", id: 0 },
    { title: "Enviada", id: 1 },

  ];


  //#region  CARREGAMENTO

  const loadAllProjetoImpressaoLista = useCallback(
    async (funcState?: LoadProjetoImpressaoState) => {
      try {
        const filteredIdsCentroCusto = evaluateParamValue(funcState?.idsCentroCusto, filters.idsCentroCusto);
        const filteredIdsProjeto = evaluateParamValue(funcState?.idsProjeto, filters.idsProjeto);
        const filteredIdsSolicitante = evaluateParamValue(funcState?.idsSolicitante, filters.idsSolicitante);
        const filteredStatus = evaluateParamValue(funcState?.status, filters.status);
        const filteredStatusEmail = evaluateParamValue(funcState?.statusEmail, filters.statusEmail);
        const filteredDataDe = evaluateParamValue(funcState?.dataDe, filters.dataDe);
        const filteredDataAte = evaluateParamValue(funcState?.dataAte, filters.dataAte);

        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllProjetoImpressaoUseCase.execute(
          filteredIdsProjeto ?? [],
          filteredIdsCentroCusto ?? [],
          filteredIdsSolicitante ?? [],
          filteredStatus ?? 3,
          filteredStatusEmail ?? 2,
          filteredDataDe ?? undefined,
          filteredDataAte ?? undefined,
          filteredPage,
          filteredRowsPerPage,

        );

        setListaImpressao(getAllProjetoImpressaoUseCase.items);

        pagination.setRowsPerPage(getAllProjetoImpressaoUseCase.pageSize);
        pagination.setTotalItems(getAllProjetoImpressaoUseCase.totalItems);
      } catch (e: unknown) {
        setListaImpressao([]);
        pushErrorNotification("Error", processError(e, "Falha ao buscar lista de projetos!"));
      }
    },
    [
      filters,
      pagination,
      getAllProjetoImpressaoUseCase,
      pushErrorNotification
    ],
  );

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      const response = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      setDdlCentroCusto(response);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Centro de Custo!"));
    }
  }, [getCentroCustoDropDownUseCase, pushErrorNotification]);


  const loadFuncionarioDropDown = useCallback(async () => {
    try {
      const response = await getFuncionariosDropDownUseCase.getFuncionariosDropDown();
      setDdlFuncionario(response.map((f) => ({ id: f.idFuncionario, title: f.nome })));
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Funcionários!"));
    }
  }, [getFuncionariosDropDownUseCase, pushErrorNotification]);


  const loadProjetosDropDown = useCallback(async () => {
    try {
      const response = await getProjetosDropDownUseCase.execute();
      setDdlProjeto(response);

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Projetos!"));
    }
  }, [getProjetosDropDownUseCase, pushErrorNotification]);

  //#endregion


  //#region PAGE START
  usePageStartBootstrap(async () => {
    try {
      await loadAllProjetoImpressaoLista();
      await loadCentroCustoDropDown();
      await loadFuncionarioDropDown();
      await loadProjetosDropDown();
      setDdlStatus(statusOptions);
      setDdlStatusEmail(statusEmailOptions);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
    }
  });

  //#endregion


  //#region  PAGINAÇÃO
  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        await loadAllProjetoImpressaoLista({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao paginar!"));
      } finally {
        setLoading(false);
      }
    },
    [
      loadAllProjetoImpressaoLista,
      pushErrorNotification
    ],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        await loadAllProjetoImpressaoLista({ page: 1, rowsPerPage: rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao paginar!"));
      } finally {
        setLoading(false);
      }
    },
    [
      loadAllProjetoImpressaoLista,
      pushErrorNotification
    ],
  );

  //#endregion



  //#region  PESQUISA

  const handleProjetoChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsProjeto(newValue);
      return async () => await loadAllProjetoImpressaoLista({ idsProjeto: newValue });
    },
    [filters, loadAllProjetoImpressaoLista],
  );

  const handleCentroCustoChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsCentroCusto(newValue);
      return async () => await loadAllProjetoImpressaoLista({ idsCentroCusto: newValue });
    },
    [filters, loadAllProjetoImpressaoLista],
  );

  const handleSolicitanteChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsSolicitante(newValue);
      return async () => await loadAllProjetoImpressaoLista({ idsSolicitante: newValue });
    },
    [filters, loadAllProjetoImpressaoLista],
  );

  const handleStatusChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number) => {
      filters.setStatus(newValue);
      return async () => await loadAllProjetoImpressaoLista({ status: newValue });
    },
    [filters, loadAllProjetoImpressaoLista],
  );

  const handleStatusEmailChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number) => {
      filters.setStatusEmail(newValue);
      return async () => await loadAllProjetoImpressaoLista({ statusEmail: newValue });
    },
    [filters, loadAllProjetoImpressaoLista],
  );

  const handleDataChange = useDebouncedCallback(
    debouncedLoad,
    (de: Date | null, ate: Date | null) => {
      filters.setPeriodo(de, ate);
      return async () => await loadAllProjetoImpressaoLista({ dataDe: de, dataAte: ate });
    },
    [filters, loadAllProjetoImpressaoLista],
  );

  const handleClear = useCallback(async () => {
    filters.clear();
    await loadAllProjetoImpressaoLista({
      idsProjeto: [],
      idsCentroCusto: [],
      idsSolicitante: [],
      status: undefined,
      statusEmail: undefined,
      dataDe: null,
      dataAte: null,

      page: 1,
      rowsPerPage: pagination.rowsPerPage,
    });
  }, [
    filters,
    loadAllProjetoImpressaoLista,
    pagination.rowsPerPage]);

  //#endregion

  //#region CRIAÇÃO E EDIÇÃO

  const handleEdit = useCallback(
    (id: number | null, openState: boolean) => {
      if (!openState) window.open(`/Impressao_cadastrogerenciar?id=${id}&visualizar=false`, "_blank", "noopener,noreferrer");
      else backOrNavigate(`/Impressao_cadastrogerenciar?id=${id}&visualizar=false`);
    },
    [backOrNavigate],
  );

  const handleCreate = useCallback(() => backOrNavigate(`/Impressao_cadastrogerenciar?visualizar=false`), [backOrNavigate]);

  //#endregion


  //#region  EMAIL 

  const handleEmailOpenState = useCallback(async (idProjetoImpressao: number | null, openState: boolean) => {
    if (openState) {

      if (idProjetoImpressao) {

        const result = await getEmailImpressaoParametersUseCase.execute(idProjetoImpressao);

        setEmailSendTo("");
        setEmailSubject("Solicitação de Impressão - " + result?.projeto);
        setEmailAttachment(`arquivo_${result?.projeto}.pdf`);

        const valores = {
          EMPRESA: result?.empresa,
          PROJETO: result?.projeto,
          CENTRO_CUSTO: result?.centroCusto,
          DATA_SOLICITACAO: result?.dataSolicitacao,
          FUNCIONARIO_SOLICITANTE: result?.funcionarioSolicitante,
          DATA_APROVACAO: result?.dataAprovacao,
          FUNCIONARIO_APROVACAO: result?.funcionarioAprovacao,
          OBSERVACAO: result?.observacao,
          QUANTIDADE: result?.quantidade,
          COR: result?.cor,
          TAMANHO: result?.tamanho,
          FORNECEDOR: result?.fornecedor,

          DISCIPLINA: result?.disciplina,
          ARQUIVO_DISCIPLINA: result?.disciplinaArquivo,
        };

        var body = emailTemplates.projetoImpressao;

        Object.entries(valores).forEach(([key, value]) => {
          body = body.replaceAll(`{${key}}`, value ?? '');
        });

        setEmailBody(body);
        setSelectedIdProjetoImpressao(idProjetoImpressao);

      } else {
        setSelectedIdProjetoImpressao(null);
      }
    }
    setOpenEmailModal(openState);
  }, [getEmailImpressaoParametersUseCase]);



  const handleEmailSubmit = useCallback(async (form: EmailForm) => {
    if (selectedIdProjetoImpressao) {
      try {
        setLoading(true);
        await sendEmailProjetoImpressaoUseCase
          .execute(
            selectedIdProjetoImpressao!,
            form.subject,
            form.sendTo,
            form.body,
            form.attachment,
          );

        pushInfoNotification("Sucesso", "Email enviado com sucesso!");
        setOpenEmailModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao enviar email!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    selectedIdProjetoImpressao,
    sendEmailProjetoImpressaoUseCase,
    pushErrorNotification,
    pushInfoNotification
  ]);


  //#endregion



  return {
    listaImpressao,
    filters,
    pagination,
    loading,
    ddlCentroCusto: ddlCentroCusto ?? [],
    ddlProjeto: ddlProjeto ?? [],
    ddlFuncionario: ddlFuncionario ?? [],
    ddlStatus: ddlStatus ?? [],
    ddlStatusEmail: ddlStatusEmail ?? [],



    openEmailModal,
    emailSendTo,
    emailSubject,
    emailAttachment,
    emailBody,


    handleEmailOpenState,
    handleEmailSubmit,
    loadAllProjetoImpressaoLista,
    handlePageChange,
    handleRowsPerPageChange,
    handleClear,
    handleProjetoChange,
    handleCentroCustoChange,
    handleSolicitanteChange,
    handleStatusChange,
    handleStatusEmailChange,
    handleDataChange,
    handleCreate,
    handleEdit,

  };
};
